import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Stack } from '@mui/material';
import {
  AutocompleteCDS,
  CheckboxCDS,
  onAutocompleteChangeInForm,
  TextFieldCDS,
} from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import { createLoanExceptionAction, updateLoanExceptionAction } from 'store/loanExceptions/actions';
import { LoanExceptionLinkedEntityType } from 'store/loanExceptions/types';

import {
  loanExceptionCategoryOptions,
  LoanExceptionStatusLabels,
  noValidateInputProps,
} from '../Exceptions.constants';
import { ExceptionsTestIds } from '../Exceptions.testIds';

import { NO_SUB_CATEGORIES_HELPER_TEXT } from './ExceptionForm.constants';
import { formStackSx } from './ExceptionForm.styles';
import { buildInitialFormState, getSubCategoryOptions } from './ExceptionForm.utils';

const ExceptionForm = ({ loanId, applicationId = null, exception = null, subCategories = [] }) => {
  const dispatch = useDispatch();
  const isEdit = !!exception?.id;

  const initialFormState = useMemo(
    () => buildInitialFormState({ exception, subCategories }),
    [exception, subCategories],
  );

  const { form, handleChange, setInForm, setMultipleInForm } = useForm(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categoryValue = form.category?.value || '';

  const subCategoryOptions = useMemo(
    () => getSubCategoryOptions(subCategories, categoryValue),
    [subCategories, categoryValue],
  );

  const isValid = !!form.category && !!form.subject.trim() && !!form.justification.trim();

  const onCategoryChange = useCallback(
    (e, option) => {
      setMultipleInForm({ category: option || null, subCategory: null });
    },
    [setMultipleInForm],
  );

  const onSubCategoryChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ setInForm })(e, option);
    },
    [setInForm],
  );

  const onSubmit = useCallback(async () => {
    if (!isValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEdit) {
        await dispatch(
          updateLoanExceptionAction({
            id: exception.id,
            payload: {
              id: exception.id,
              subCategoryId: form.subCategory?.value || null,
              subject: form.subject.trim(),
              justification: form.justification.trim(),
              hmdaImpact: form.hmdaImpact,
              hmdaCodeId: exception.hmdaCodeId || null,
            },
          }),
        );
      }

      if (!isEdit) {
        await dispatch(
          createLoanExceptionAction({
            payload: {
              loanId,
              applicationId,
              category: form.category.value,
              subCategoryId: form.subCategory?.value || null,
              subject: form.subject.trim(),
              justification: form.justification.trim(),
              hmdaImpact: form.hmdaImpact,
              hmdaCodeId: null,
              linkedEntityType: LoanExceptionLinkedEntityType.MANUAL,
              linkedEntityKey: null,
            },
          }),
        );
      }

      dispatch(closeDialogAction());
    } catch (error) {
      dispatch(
        showMessageAction({
          variant: 'error',
          message: error?.response?.data?.message || 'Could not save exception',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, dispatch, exception, form, isEdit, isSubmitting, isValid, loanId]);

  return (
    <Stack sx={formStackSx}>
      <AutocompleteCDS
        required
        dataTestId={ExceptionsTestIds.CATEGORY_SELECT}
        disabled={isEdit}
        label="Category"
        name="category"
        optionKey="value"
        optionName="label"
        options={loanExceptionCategoryOptions}
        value={form.category}
        onChange={onCategoryChange}
      />

      <AutocompleteCDS
        dataTestId={ExceptionsTestIds.SUB_CATEGORY_INPUT}
        disabled={!categoryValue || subCategoryOptions.length === 0}
        helperText={
          categoryValue && subCategoryOptions.length === 0 ? NO_SUB_CATEGORIES_HELPER_TEXT : ''
        }
        label="Sub-category"
        name="subCategory"
        optionKey="value"
        optionName="label"
        options={subCategoryOptions}
        value={form.subCategory}
        onChange={onSubCategoryChange}
      />

      <TextFieldCDS
        required
        dataTestId={ExceptionsTestIds.REASON_INPUT}
        inputProps={noValidateInputProps}
        label="Subject"
        name="subject"
        value={form.subject}
        onChange={handleChange}
      />

      <TextFieldCDS
        multiline
        required
        dataTestId={ExceptionsTestIds.JUSTIFICATION_INPUT}
        inputProps={noValidateInputProps}
        label="Justification"
        minRows={3}
        name="justification"
        value={form.justification}
        onChange={handleChange}
      />

      <CheckboxCDS
        noValidate
        dataTestId={ExceptionsTestIds.AFFECTS_HMDA_CHECKBOX}
        label="Affects HMDA reporting"
        name="hmdaImpact"
        value={form.hmdaImpact}
        onChange={handleChange}
      />

      {isEdit && (
        <TextFieldCDS
          disabled
          dataTestId={ExceptionsTestIds.CURRENT_STATUS_INPUT}
          label="Current status"
          name="status"
          value={LoanExceptionStatusLabels[exception.status] || exception.status}
        />
      )}

      <Button
        data-testid={ExceptionsTestIds.SUBMIT_BUTTON}
        disabled={!isValid || isSubmitting}
        variant="contained"
        onClick={onSubmit}
      >
        {isEdit ? 'Save' : 'Submit'}
      </Button>
    </Stack>
  );
};

ExceptionForm.propTypes = {
  loanId: PropTypes.string.isRequired,
  applicationId: PropTypes.string,
  exception: PropTypes.shape({
    id: PropTypes.string,
    category: PropTypes.string,
    subCategoryId: PropTypes.string,
    subject: PropTypes.string,
    justification: PropTypes.string,
    hmdaImpact: PropTypes.bool,
    hmdaCodeId: PropTypes.string,
    status: PropTypes.string,
  }),
  subCategories: PropTypes.array,
};

export default ExceptionForm;
